/**
 * Wishlist Module
 *
 * Provides complete wishlist functionality using localStorage persistence.
 *
 * Custom Elements:
 *   <wishlist-button>        — Heart toggle on product cards
 *   <wishlist-icon>          — Header icon with count badge
 *   <wishlist-drawer-component> — Side-panel drawer
 */

// ─── Constants ────────────────────────────────────────────────────────────────

const WISHLIST_KEY = 'n3vo_wishlist_items';
const WISHLIST_UPDATE_EVENT = 'wishlist:update';

// ─── Wishlist Store ───────────────────────────────────────────────────────────

const WishlistStore = {
  /** @returns {WishlistItem[]} */
  getItems() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch {
      return [];
    }
  },

  /** @param {WishlistItem[]} items */
  setItems(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    document.dispatchEvent(
      new CustomEvent(WISHLIST_UPDATE_EVENT, {
        bubbles: false,
        detail: { items, count: items.length },
      })
    );
  },

  /** @param {WishlistItem} product @returns {boolean} true if newly added */
  addItem(product) {
    const items = this.getItems();
    if (!items.find((i) => String(i.id) === String(product.id))) {
      items.push(product);
      this.setItems(items);
      return true;
    }
    return false;
  },

  /** @param {string|number} productId */
  removeItem(productId) {
    const items = this.getItems().filter(
      (i) => String(i.id) !== String(productId)
    );
    this.setItems(items);
  },

  /** @param {string|number} productId @returns {boolean} */
  hasItem(productId) {
    return this.getItems().some((i) => String(i.id) === String(productId));
  },

  /** @param {string|number} productId */
  markItemAddedToCart(productId) {
    const items = this.getItems().map((item) =>
      String(item.id) === String(productId)
        ? { ...item, addedToCart: true }
        : item
    );
    this.setItems(items);
  },

  clear() {
    this.setItems([]);
  },
};

// ─── Toast Notification ───────────────────────────────────────────────────────

function showToast(message) {
  let container = document.getElementById('wishlist-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'wishlist-toast-container';
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'true');
    container.setAttribute('role', 'status');
    document.body.appendChild(container);
  }

  const toast = document.createElement('div');
  toast.className = 'wishlist-toast';
  toast.innerHTML =
    `<svg class="wishlist-toast__icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor" stroke="none"/></svg>` +
    `<span>${escHtml(message)}</span>`;
  container.appendChild(toast);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => toast.classList.add('wishlist-toast--visible'));
  });

  setTimeout(() => {
    toast.classList.add('wishlist-toast--hide');
    toast.addEventListener('transitionend', () => toast.remove(), {
      once: true,
    });
  }, 2800);
}

// ─── Security helpers ─────────────────────────────────────────────────────────

/** Escape a string for safe insertion into HTML text / attribute content */
function escHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Return a URL only if it is relative (same origin).
 * Prevents open-redirect or javascript: injection from stored data.
 */
function safeUrl(url) {
  if (!url) return '#';
  try {
    const parsed = new URL(String(url), window.location.origin);
    if (parsed.origin !== window.location.origin) return '#';
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return '#';
  }
}

// ─── Wishlist Button ──────────────────────────────────────────────────────────

class WishlistButtonElement extends HTMLElement {
  connectedCallback() {
    this.#updateState();
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(WISHLIST_UPDATE_EVENT, this.#onUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(WISHLIST_UPDATE_EVENT, this.#onUpdate);
  }

  get #productId() {
    return this.dataset.productId;
  }

  get #productData() {
    return {
      id: this.dataset.productId,
      title: this.dataset.productTitle,
      price: this.dataset.productPrice,
      priceRaw: this.dataset.productPriceRaw || '0',
      compareAtPrice: this.dataset.productComparePrice || '',
      compareAtPriceRaw: this.dataset.productComparePriceRaw || '0',
      rating: this.dataset.productRating || '',
      reviewCount: this.dataset.productReviewCount || '0',
      image: this.dataset.productImage,
      url: this.dataset.productUrl,
      variantId: this.dataset.productVariantId || null,
    };
  }

  #onUpdate = () => this.#updateState();

  #handleClick = (event) => {
    const btn = event.target.closest('.wishlist-btn');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    this.#toggle();
  };

  #toggle() {
    const wasWishlisted = WishlistStore.hasItem(this.#productId);
    const btn = this.querySelector('.wishlist-btn');

    if (wasWishlisted) {
      WishlistStore.removeItem(this.#productId);
      showToast('Removed from wishlist');
    } else {
      WishlistStore.addItem(this.#productData);
      btn?.classList.add('wishlist-btn--pulse');
      btn?.addEventListener(
        'animationend',
        () => btn.classList.remove('wishlist-btn--pulse'),
        { once: true }
      );
      showToast('Added to Wishlist');
    }
  }

  #updateState() {
    const btn = this.querySelector('.wishlist-btn');
    if (!btn) return;
    const active = WishlistStore.hasItem(this.#productId);
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute(
      'aria-label',
      active ? 'Remove from wishlist' : 'Add to wishlist'
    );
    btn.classList.toggle('wishlist-btn--active', active);
    const path = btn.querySelector('.wishlist-heart-path');
    if (path) path.setAttribute('fill', active ? 'currentColor' : 'none');
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButtonElement);
}

// ─── Wishlist Icon (Header) ───────────────────────────────────────────────────

class WishlistIconElement extends HTMLElement {
  connectedCallback() {
    this.#updateCount();
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(WISHLIST_UPDATE_EVENT, this.#onUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(WISHLIST_UPDATE_EVENT, this.#onUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  #onUpdate = () => this.#updateCount();
  #onPageShow = () => this.#updateCount();

  #handleClick = () => {
    const drawer = /** @type {WishlistDrawerElement|null} */ (
      document.getElementById('wishlist-drawer-component')
    );
    if (drawer && typeof drawer.openDrawer === 'function') {
      drawer.openDrawer();
    } else {
      window.location.href = '/pages/wishlist';
    }
  };

  #updateCount() {
    const count = WishlistStore.getItems().length;
    const badge = this.querySelector('.wishlist-badge');
    const countEl = this.querySelector('.wishlist-badge__count');
    if (!badge || !countEl) return;
    countEl.textContent = count > 99 ? '99+' : String(count);
    badge.classList.toggle('wishlist-badge--visible', count > 0);
  }
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIconElement);
}

// ─── Wishlist Drawer ──────────────────────────────────────────────────────────

class WishlistDrawerElement extends HTMLElement {
  /** @type {HTMLDialogElement|null} */
  dialog = null;
  scrollY = 0;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    document.addEventListener(WISHLIST_UPDATE_EVENT, this.onUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('storage', this.onStorage);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_UPDATE_EVENT, this.onUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('storage', this.onStorage);
  }

  onUpdate = () => this.render();
  onPageShow = () => this.render();
  onStorage = (event) => {
    if (event.key === WISHLIST_KEY) this.render();
  };

  openDrawer() {
    if (!this.dialog || this.dialog.open) return;
    this.scrollY = window.scrollY;
    document.body.style.cssText +=
      `;position:fixed;top:-${this.scrollY}px;width:100%;overflow-y:scroll;`;
    this.render();
    this.dialog.showModal();
    this.dialog.addEventListener('click', this.onBackdropClick);
    requestAnimationFrame(() => {
      this.querySelector('.wishlist-drawer__close-btn')?.focus();
    });
  }

  closeDrawer() {
    if (!this.dialog?.open) return;
    this.dialog.close();
    this.dialog.removeEventListener('click', this.onBackdropClick);
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflowY = '';
    window.scrollTo(0, this.scrollY);
  }

  onBackdropClick = (event) => {
    const rect = this.dialog.getBoundingClientRect();
    const outside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;
    if (outside) this.closeDrawer();
  };

  render() {
    const items = WishlistStore.getItems();
    const listEl = this.querySelector('.wishlist-drawer__list');
    const emptyEl = this.querySelector('.wishlist-drawer__empty');
    const contentEl = this.querySelector('.wishlist-drawer__content');
    if (!listEl) return;

    if (items.length === 0) {
      listEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      if (contentEl) contentEl.hidden = true;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (contentEl) contentEl.hidden = false;

    listEl.innerHTML = items
      .map((item) => {
        const ratingValue = Number(item.rating || '0');
        const filledStars = Math.min(5, Math.max(0, Math.round(ratingValue)));
        const stars = '★'.repeat(filledStars) + '☆'.repeat(5 - filledStars);
        const reviewMarkup =
          '<div class="wishlist-item__reviews" aria-label="' +
          (filledStars > 0
            ? 'Rated ' + ratingValue.toFixed(1) + ' out of 5'
            : 'No ratings yet') +
          '">' +
          '<span class="wishlist-item__stars">' + escHtml(stars) + '</span>' +
          '<span class="wishlist-item__review-count">(' + escHtml(String(item.reviewCount || '0')) + ')</span>' +
          '</div>';
        const discountMarkup =
          Number(item.compareAtPriceRaw) > Number(item.priceRaw)
            ? '<span class="wishlist-item__discount">-' +
              Math.round(
                ((Number(item.compareAtPriceRaw) - Number(item.priceRaw)) / Number(item.compareAtPriceRaw)) * 100
              ) +
              '%</span>'
            : '';
        const alreadyAdded = Boolean(item.addedToCart);

        return /* html */ `
      <li class="wishlist-item" data-id="${escHtml(String(item.id))}">
        <a href="${safeUrl(item.url)}" class="wishlist-item__img-wrap" tabindex="-1" aria-hidden="true">
          <img
            src="${escHtml(item.image)}"
            alt="${escHtml(item.title)}"
            class="wishlist-item__img"
            loading="lazy"
            width="120"
            height="150"
          >
        </a>
        <div class="wishlist-item__body">
          <a href="${safeUrl(item.url)}" class="wishlist-item__name">${escHtml(item.title)}</a>
          ${reviewMarkup}
          <div class="wishlist-item__price-row">
            <span class="wishlist-item__selling-price">${escHtml(item.price)}</span>
            ${item.compareAtPrice ? `<span class="wishlist-item__mrp">${escHtml(item.compareAtPrice)}</span>` : ''}
            ${discountMarkup}
          </div>
          <div class="wishlist-item__actions">
            ${alreadyAdded
              ? `<button class="wishlist-item__add-btn wishlist-item__add-btn--added" type="button" disabled><span>Added</span><span class="wishlist-item__add-btn__icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12.5l2.5 2.5L16 10"/></svg></span></button>`
              : `<button
                class="wishlist-item__add-btn"
                type="button"
                data-add-to-cart
                data-product-id="${escHtml(String(item.id))}"
                data-variant-id="${escHtml(item.variantId || '')}"
              >
                <span>Add to Cart</span>
                <span class="wishlist-item__add-btn__icon" aria-hidden="true">→</span>
              </button>`}
            <button
              class="wishlist-item__remove-btn"
              data-remove="${escHtml(String(item.id))}"
              aria-label="Remove ${escHtml(item.title)} from wishlist"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6"/>
              </svg>
            </button>
          </div>
        </div>
      </li>
    `
      })
      .join('');

    listEl.querySelectorAll('[data-remove]').forEach((btn) => {
      btn.addEventListener('click', () =>
        WishlistStore.removeItem(btn.dataset.remove)
      );
    });

    listEl.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const variantId = button.dataset.variantId;
        const productId = button.dataset.productId || button.closest('[data-id]')?.dataset.id;
        if (!variantId || !productId) return;

        button.disabled = true;
        button.innerHTML = '<span>Adding…</span>';

        try {
          const response = await fetch(
            (window.Shopify?.routes?.root ?? '/') + 'cart/add.js',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 }),
            }
          );

          if (!response.ok) throw new Error('Add to cart failed');
          const addedItem = await response.json();

          showToast('Added to cart');
          button.classList.add('wishlist-item__add-btn--added');
          button.innerHTML = '<span>Added</span><span class="wishlist-item__add-btn__icon" aria-hidden="true">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<circle cx="12" cy="12" r="10" />' +
            '<path d="M8 12.5l2.5 2.5L16 10" />' +
            '</svg>' +
            '</span>';
          WishlistStore.markItemAddedToCart(productId);
          // Close wishlist drawer if it's open so it doesn't stay visible
          // while the cart drawer opens.
          try {
            const liveDrawer = document.getElementById('wishlist-drawer-component');
            if (liveDrawer && typeof liveDrawer.closeDrawer === 'function') {
              liveDrawer.closeDrawer();
            }
          } catch (_) {}

          try {
            const cartResp = await fetch((window.Shopify?.routes?.root ?? '/') + 'cart.js');
            if (cartResp.ok) {
              const cart = await cartResp.json();
              // Dispatch cart:update but mark the source so the cart-drawer
              // will open (avoid using 'wishlist' or 'wishlist-drawer' which
              // the cart drawer treats as internal and will not auto-open).
              document.dispatchEvent(
                new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    resource: cart,
                    sourceId: 'wishlist',
                    data: {
                      source: 'wishlist-inline',
                      itemCount: cart.item_count ?? 1,
                      variantId: variantId,
                    },
                  },
                })
              );
            } else {
              document.dispatchEvent(
                new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    resource: {},
                    sourceId: 'wishlist',
                    data: { source: 'wishlist-inline', itemCount: 1, variantId: variantId },
                  },
                })
              );
            }
          } catch (e) {
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: {},
                  sourceId: 'wishlist',
                  data: { source: 'wishlist-inline', itemCount: 1, variantId: variantId },
                },
              })
            );
          }
        } catch (error) {
          showToast('Could not add item to cart');
          button.disabled = false;
          button.innerHTML = '<span>Add to Cart</span><span class="wishlist-item__add-btn__icon" aria-hidden="true">→</span>';
        }
      });
    });
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerElement);
}

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   price: string,
 *   priceRaw: string,
 *   compareAtPrice: string,
 *   compareAtPriceRaw: string,
 *   rating: string,
 *   reviewCount: string,
 *   image: string,
 *   url: string,
 *   variantId: string|null,
 *   addedToCart?: boolean,
 * }} WishlistItem
 */
